import { Segmented } from "@components/ui/Segmented"
import { formatRelativeDate } from "@lib/intl"
import { Download, FlaskConical } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"

import type { TrimesterKey } from "../types"

interface Props {
  trimester: TrimesterKey
  onTrimesterChange: (next: TrimesterKey) => void
  institutionName: string
  generatedAt: string
}

const OPTIONS: ReadonlyArray<TrimesterKey> = ["current", "previous"]

export const DirectionHeader = memo(function DirectionHeader({
  trimester,
  onTrimesterChange,
  institutionName,
  generatedAt,
}: Props) {
  const { t } = useTranslation("direction")

  return (
    <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div className="space-y-2">
        <h1 className="text-ink text-xl font-bold md:text-2xl">{t("page.title")}</h1>
        <p className="text-ink-subtle text-sm">
          {t("page.subtitle", {
            institution: institutionName,
            updated: formatRelativeDate(generatedAt),
          })}
        </p>
        {/* Every number on this page comes from `mocks.ts` (see api.ts). It
            rendered under "Actualizado hace 2 horas" with the only hint in a
            disabled button's tooltip, so a director could act on invented
            figures (PRD-59 Etapa B). Remove when the endpoint is real. */}
        <p
          role="note"
          className="rounded-control bg-attention-soft text-attention-ink inline-flex items-start gap-2 px-3 py-2 text-sm"
        >
          <FlaskConical className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            <span className="font-semibold">{t("sample.label")}</span> {t("sample.body")}
          </span>
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Was a hand-rolled role="listbox" of buttons with no arrow keys. */}
        <Segmented<TrimesterKey>
          ariaLabel={t("trimester.label")}
          value={trimester}
          onChange={onTrimesterChange}
          options={OPTIONS.map((opt) => ({ value: opt, label: t(`trimester.${opt}`) }))}
        />

        <button
          type="button"
          disabled
          aria-disabled="true"
          title={t("export.tooltip")}
          className="rounded-control border-border bg-inset text-ink-subtle inline-flex min-h-11 cursor-not-allowed items-center gap-2 border px-3 text-sm md:min-h-9"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          <span>{t("export.label")}</span>
        </button>
      </div>
    </header>
  )
})
